import type { ListMembershipsResponse } from '@bliks-io/contracts';
import type { AuthSessionData } from '@/stores/auth-store';

export type SlugMismatchResolution =
  | { action: 'switched' }
  | { action: 'redirect-to-session-slug' };

export interface SlugMismatchDeps {
  fetchMemberships: () => Promise<ListMembershipsResponse>;
  switchTenant: (tenantId: string) => Promise<AuthSessionData>;
  adoptSession: (session: AuthSessionData) => void;
  loadFullPage: (url: string) => void;
}

/**
 * Resolve a URL-slug / session-slug mismatch by switching the session into
 * the URL's workspace when the caller holds an ACTIVE membership there.
 *
 * ACTIVE only, deliberately: switching into a pending membership accepts the
 * invite, and a URL visit must never silently accept one — pending invitees
 * enter via the workspace picker, which accepts deliberately. The super-admin
 * pseudo-tenant is excluded the same way: a link visit must never silently
 * re-scope a staff member's tenant session into the admin console.
 *
 * On a successful switch the continuation is a FULL page load of the original
 * href, never an SPA redirect: every cached byte is tenant-scoped, the in-app
 * clear/invalidate/navigate sequence raced itself (see useSwitchWorkspace),
 * and an SPA continuation would re-run beforeLoad against the OLD tenant's
 * cached profile/orgSettings (`ensureQueryData` never refetches) — looping
 * forever on a stale membership slug. The reload also resolves the
 * renamed-slug case for free: the fresh document boots from the persisted new
 * session and refetches memberships.
 *
 * Multi-tab semantics: the auth store persists one shared session in
 * localStorage, so an auto-switch here means other tabs adopt the new tenant
 * on their next full load — exactly like a manual picker switch. Switches
 * only fire on user-initiated navigations and each tab converges after its
 * own reload, so no ping-pong loop exists; no cross-tab ownership machinery.
 *
 * Every miss BEFORE the switch resolves (no active membership, matched tenant
 * IS the session tenant — the renamed-slug edge —, switch/fetch failure)
 * falls back to today's behavior: bounce to the session tenant's dashboard.
 */
export function resolveSlugMismatch(
  params: { urlSlug: string; sessionTenantId: string | null; href: string },
  deps: SlugMismatchDeps
): Promise<SlugMismatchResolution> {
  // Single-flight: concurrent beforeLoad runs (parallel loaders, double
  // navigation) must not issue competing switch POSTs — later callers share
  // the first run's outcome.
  inFlightResolution ??= attemptSwitch(params, deps).finally(() => {
    inFlightResolution = null;
  });
  return inFlightResolution;
}

let inFlightResolution: Promise<SlugMismatchResolution> | null = null;

async function attemptSwitch(
  params: { urlSlug: string; sessionTenantId: string | null; href: string },
  deps: SlugMismatchDeps
): Promise<SlugMismatchResolution> {
  const redirectToSessionSlug = { action: 'redirect-to-session-slug' } as const;

  let session: AuthSessionData;
  try {
    const { memberships } = await deps.fetchMemberships();
    const target = memberships.find(
      (membership) =>
        membership.slug === params.urlSlug &&
        membership.status === 'active' &&
        membership.tenantType !== 'super'
    );
    if (!target || target.tenantId === params.sessionTenantId) {
      return redirectToSessionSlug;
    }
    // Covers 403 (membership revoked mid-flight), 401 (the global handler
    // may already be tearing the session down) and network failures alike.
    session = await deps.switchTenant(target.tenantId);
  } catch {
    return redirectToSessionSlug;
  }

  // Past this point the server has already re-scoped the session (and the
  // adopted session persists for every tab), so falling back against the
  // PRE-switch slug snapshot would silently strand the user in a workspace
  // their session no longer matches — a throw here must surface, never
  // degrade into the redirect.
  deps.adoptSession(session);
  deps.loadFullPage(params.href);
  return { action: 'switched' };
}
